import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { MapPin, Phone, Mail, CheckCircle, Droplets, Wrench, ShoppingBag, Calendar, ArrowRight } from 'lucide-react';
import SEOHead from '../components/SEOHead';

// Zone data for SEO
const ZONES = {
    'draguignan': {
        name: 'Draguignan',
        department: '83',
        description: 'Expert piscine à Draguignan et ses environs. Analyse d\'eau, produits de traitement, recherche de fuite et entretien.',
        nearby: ['Trans-en-Provence', 'Le Muy', 'Flayosc', 'Lorgues', 'Figanières'],
        distance: '10 min',
        postalCodes: ['83300']
    },
    'frejus': {
        name: 'Fréjus',
        department: '83',
        description: 'Pisciniste professionnel intervenant à Fréjus. Vente de produits, diagnostic et réparation de piscine.',
        nearby: ['Saint-Raphaël', 'Puget-sur-Argens', 'Roquebrune-sur-Argens', 'Les Adrets-de-l\'Estérel'],
        distance: '25 min',
        postalCodes: ['83600', '83370']
    },
    'saint-raphael': {
        name: 'Saint-Raphaël',
        department: '83',
        description: 'O.C.A Piscine intervient à Saint-Raphaël pour tous vos besoins : entretien, réparation, produits de traitement.',
        nearby: ['Fréjus', 'Agay', 'Boulouris', 'Valescure', 'Les Adrets-de-l\'Estérel'],
        distance: '30 min',
        postalCodes: ['83700', '83530']
    },
    'sainte-maxime': {
        name: 'Sainte-Maxime',
        department: '83',
        description: 'Votre spécialiste piscine à Sainte-Maxime. Analyse d\'eau gratuite, produits CTX et O.C.A PRO, interventions rapides.',
        nearby: ['Grimaud', 'Plan-de-la-Tour', 'Roquebrune-sur-Argens', 'Les Issambres'],
        distance: '35 min',
        postalCodes: ['83120']
    },
    'le-muy': {
        name: 'Le Muy',
        department: '83',
        description: 'Pisciniste au Muy. Recherche de fuite, entretien régulier, vente de produits pour piscine.',
        nearby: ['Roquebrune-sur-Argens', 'Les Arcs', 'Trans-en-Provence', 'Puget-sur-Argens'],
        distance: '15 min',
        postalCodes: ['83490']
    },
    'lorgues': {
        name: 'Lorgues',
        department: '83',
        description: 'Expert piscine à Lorgues. Diagnostic, réparation, entretien et vente de produits de qualité professionnelle.',
        nearby: ['Taradeau', 'Le Thoronet', 'Vidauban', 'Flayosc', 'Salernes'],
        distance: '15 min',
        postalCodes: ['83510']
    },
    'trans-en-provence': {
        name: 'Trans-en-Provence',
        department: '83',
        description: 'O.C.A Piscine à Trans-en-Provence. Tous services piscine : analyse, traitement, réparation, recherche de fuite.',
        nearby: ['Draguignan', 'La Motte', 'Le Muy', 'Les Arcs'],
        distance: '12 min',
        postalCodes: ['83720']
    },
    'vidauban': {
        name: 'Vidauban',
        department: '83',
        description: 'Spécialiste piscine à Vidauban. Produits de traitement, entretien, dépannage et conseils personnalisés.',
        nearby: ['Les Arcs', 'Le Luc', 'Le Thoronet', 'Lorgues'],
        distance: '20 min',
        postalCodes: ['83550']
    },
    'les-arcs': {
        name: 'Les Arcs-sur-Argens',
        department: '83',
        description: 'Votre pisciniste aux Arcs-sur-Argens. Intervention rapide, produits professionnels, expertise locale.',
        nearby: ['Vidauban', 'Le Muy', 'Trans-en-Provence', 'Taradeau'],
        distance: '18 min',
        postalCodes: ['83460']
    },
    'grimaud': {
        name: 'Grimaud',
        department: '83',
        description: 'Expert piscine à Grimaud et Port Grimaud. Entretien haut de gamme, produits premium, intervention sur site.',
        nearby: ['Sainte-Maxime', 'Cogolin', 'La Garde-Freinet', 'Saint-Tropez'],
        distance: '40 min',
        postalCodes: ['83310']
    }
};

const SERVICES = [
    { icon: Droplets, title: 'Analyse d\'eau', desc: 'Analyse gratuite et recommandations personnalisées' },
    { icon: ShoppingBag, title: 'Produits de traitement', desc: 'Gamme CTX et O.C.A PRO professionnelle' },
    { icon: Wrench, title: 'Recherche de fuite', desc: 'Détection et réparation de fuites' },
    { icon: Calendar, title: 'Entretien régulier', desc: 'Contrats d\'entretien sur mesure' }
];

export default function ZonePage() {
    const { zone } = useParams();
    const zoneData = ZONES[zone];

    if (!zoneData) {
        return (
            <div className="min-h-screen pt-20 pb-12 flex items-center justify-center">
                <Card className="glass rounded-3xl max-w-md w-full mx-4">
                    <CardContent className="py-12 text-center">
                        <MapPin className="h-16 w-16 text-muted-foreground/50 mx-auto mb-4" />
                        <h1 className="font-heading text-2xl font-bold mb-2">Zone non trouvée</h1>
                        <p className="text-muted-foreground mb-6">
                            Cette zone n'est pas encore référencée.
                        </p>
                        <Link to="/">
                            <Button className="rounded-full">Retour à l'accueil</Button>
                        </Link>
                    </CardContent>
                </Card>
            </div>
        );
    }

    const schemaData = {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "name": `O.C.A Piscine - ${zoneData.name}`,
        "description": zoneData.description,
        "areaServed": {
            "@type": "City",
            "name": zoneData.name
        },
        "telephone": "+33612041061",
        "email": "oca.directionpaca@gmail.com",
        "address": {
            "@type": "PostalAddress",
            "streetAddress": "8 Rue Roger Chieusse",
            "addressLocality": "Flayosc",
            "postalCode": "83780",
            "addressCountry": "FR"
        }
    };

    return (
        <>
            <SEOHead 
                title={`Pisciniste ${zoneData.name} (${zoneData.department}) - Entretien & Produits Piscine | O.C.A Piscine`}
                description={`${zoneData.description} Intervention rapide à ${zoneData.name} et environs. Devis gratuit au 06 51 96 14 74.`}
                keywords={`pisciniste ${zoneData.name}, piscine ${zoneData.name}, entretien piscine ${zoneData.department}, produits piscine ${zoneData.name}, recherche fuite ${zoneData.name}`}
                structuredData={[schemaData]}
            />
            
            <div className="min-h-screen pt-20 pb-12">
                {/* Hero Section */}
                <div className="bg-gradient-to-br from-primary/10 via-background to-accent/10 py-16">
                    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex items-center gap-2 text-primary mb-4">
                            <MapPin className="h-5 w-5" />
                            <span className="text-sm font-medium">Zone d'intervention</span>
                        </div>
                        <h1 className="font-heading text-4xl sm:text-5xl font-bold mb-4">
                            Pisciniste à <span className="text-primary">{zoneData.name}</span>
                        </h1>
                        <p className="text-xl text-muted-foreground max-w-2xl mb-6">
                            {zoneData.description}
                        </p>
                        <div className="flex flex-wrap gap-4">
                            <Link to="/appointment">
                                <Button size="lg" className="rounded-full gap-2">
                                    <Calendar className="h-5 w-5" />
                                    Prendre rendez-vous
                                </Button>
                            </Link>
                            <a href="tel:0651961474">
                                <Button size="lg" variant="outline" className="rounded-full gap-2">
                                    <Phone className="h-5 w-5" />
                                    06 51 96 14 74
                                </Button>
                            </a>
                        </div>
                    </div>
                </div>

                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    {/* Services */}
                    <section className="mb-16">
                        <h2 className="font-heading text-2xl font-bold mb-6">
                            Nos services à {zoneData.name}
                        </h2>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            {SERVICES.map((service, index) => (
                                <Card key={index} className="glass rounded-2xl">
                                    <CardContent className="p-6">
                                        <service.icon className="h-10 w-10 text-primary mb-4" />
                                        <h3 className="font-semibold mb-2">{service.title}</h3>
                                        <p className="text-sm text-muted-foreground">{service.desc}</p>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </section>

                    {/* Why choose us */}
                    <section className="mb-16">
                        <div className="grid lg:grid-cols-2 gap-8 items-center">
                            <div>
                                <h2 className="font-heading text-2xl font-bold mb-4">
                                    Pourquoi choisir O.C.A Piscine à {zoneData.name} ?
                                </h2>
                                <ul className="space-y-3">
                                    {[
                                        `Intervention rapide (à seulement ${zoneData.distance} de notre base)`,
                                        'Techniciens expérimentés et certifiés',
                                        'Produits professionnels CTX et O.C.A PRO',
                                        'Analyse d\'eau gratuite en magasin',
                                        'Devis gratuit et sans engagement',
                                        'Service après-vente réactif'
                                    ].map((item, index) => (
                                        <li key={index} className="flex items-start gap-3">
                                            <CheckCircle className="h-5 w-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <Card className="glass rounded-3xl">
                                <CardContent className="p-6">
                                    <h3 className="font-heading text-xl font-bold mb-4">Contactez-nous</h3>
                                    <div className="space-y-4">
                                        <a href="tel:0651961474" className="flex items-center gap-3 p-3 rounded-xl bg-primary/5 hover:bg-primary/10 transition-colors">
                                            <Phone className="h-5 w-5 text-primary" />
                                            <div>
                                                <p className="font-semibold">06 51 96 14 74</p>
                                                <p className="text-sm text-muted-foreground">Appel direct</p>
                                            </div>
                                        </a>
                                        <a href="mailto:oca.directionpaca@gmail.com" className="flex items-center gap-3 p-3 rounded-xl bg-primary/5 hover:bg-primary/10 transition-colors">
                                            <Mail className="h-5 w-5 text-primary" />
                                            <div>
                                                <p className="font-semibold">oca.directionpaca@gmail.com</p>
                                                <p className="text-sm text-muted-foreground">Par email</p>
                                            </div>
                                        </a>
                                        <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/50">
                                            <MapPin className="h-5 w-5 text-primary" />
                                            <div>
                                                <p className="font-semibold">8 Rue Roger Chieusse</p>
                                                <p className="text-sm text-muted-foreground">83780 Flayosc</p>
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </section>

                    {/* Nearby zones */}
                    <section className="mb-16">
                        <h2 className="font-heading text-2xl font-bold mb-6">
                            Nous intervenons également à proximité
                        </h2>
                        <div className="flex flex-wrap gap-2">
                            {zoneData.nearby.map((city, index) => (
                                <span key={index} className="px-4 py-2 bg-muted rounded-full text-sm">
                                    {city}
                                </span>
                            ))}
                        </div>
                    </section>

                    {/* Other zones */}
                    <section>
                        <h2 className="font-heading text-2xl font-bold mb-6">
                            Toutes nos zones d'intervention dans le Var
                        </h2>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {Object.entries(ZONES).map(([slug, data]) => (
                                <Link 
                                    key={slug} 
                                    to={`/zone/${slug}`}
                                    className={`flex items-center justify-between p-4 rounded-xl transition-colors ${
                                        slug === zone 
                                            ? 'bg-primary text-primary-foreground' 
                                            : 'bg-muted/50 hover:bg-muted'
                                    }`}
                                >
                                    <div className="flex items-center gap-2">
                                        <MapPin className="h-4 w-4" />
                                        <span className="font-medium">{data.name}</span>
                                    </div>
                                    <ArrowRight className="h-4 w-4" />
                                </Link>
                            ))}
                        </div>
                    </section>

                    {/* CTA */}
                    <section className="mt-16">
                        <Card className="glass rounded-3xl bg-gradient-to-r from-primary/10 to-accent/10">
                            <CardContent className="p-8 text-center">
                                <h2 className="font-heading text-2xl font-bold mb-4">
                                    Besoin d'un pisciniste à {zoneData.name} ?
                                </h2>
                                <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
                                    Contactez O.C.A Piscine pour tous vos besoins : entretien, réparation, 
                                    recherche de fuite, produits de traitement. Devis gratuit !
                                </p>
                                <div className="flex flex-wrap justify-center gap-4">
                                    <Link to="/appointment">
                                        <Button size="lg" className="rounded-full">
                                            Demander un devis gratuit
                                        </Button>
                                    </Link>
                                    <Link to="/shop">
                                        <Button size="lg" variant="outline" className="rounded-full">
                                            Voir nos produits
                                        </Button>
                                    </Link>
                                </div>
                            </CardContent>
                        </Card>
                    </section>
                </div>
            </div>
        </>
    );
}
